import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { exportChartAsPNG, exportChartAsSVG, exportChartAsPDF } from '../../utils/chartExport';
import { useToast } from './Toast';
import './ChartExportButton.css';
import '../../styles/ChartStyles.css';

/**
 * Chart Export Button Component
 * Provides export functionality for charts (PNG, SVG, PDF)
 * @param {Object} props - Component props
 * @param {HTMLElement|React.RefObject} props.chartRef - Reference to chart element
 * @param {string} [props.filename] - Base filename (without extension)
 * @param {Array} [props.formats] - Available formats ['png', 'svg', 'pdf']
 * @param {string} [props.className] - Additional CSS classes
 */
const ChartExportButton = ({ 
  chartRef, 
  filename = 'chart', 
  formats = ['png', 'svg', 'pdf'],
  className = '' 
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const { showToast } = useToast();

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const getChartElement = () => {
    if (!chartRef) return null;
    
    // Handle React ref object
    if (chartRef.current) {
      return chartRef.current;
    }
    
    // Handle direct element
    if (chartRef instanceof HTMLElement) {
      return chartRef;
    }
    
    return null;
  };

  const handleExport = async (format) => {
    const element = getChartElement();
    
    if (!element) {
      showToast('Chart element not found', 'error');
      return;
    }

    setIsExporting(true);
    setShowMenu(false);

    try {
      switch (format) {
        case 'png':
          await exportChartAsPNG(element, filename);
          showToast('Chart exported as PNG', 'success');
          break;
        case 'svg':
          exportChartAsSVG(element, filename);
          showToast('Chart exported as SVG', 'success');
          break;
        case 'pdf':
          await exportChartAsPDF(element, filename);
          showToast('Chart exported as PDF', 'success');
          break;
        default:
          showToast(`Export format ${format} not supported`, 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showToast(`Failed to export chart: ${error.message}`, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const formatLabels = {
    png: 'PNG Image',
    svg: 'SVG Vector',
    pdf: 'PDF Document'
  };

  const formatIcons = {
    png: 'bi-file-earmark-image',
    svg: 'bi-filetype-svg',
    pdf: 'bi-file-earmark-pdf'
  };

  if (formats.length === 0) {
    return null;
  }

  return (
    <div className={`chart-export-button ${className}`} ref={menuRef}>
      <button
        className="export-trigger"
        onClick={() => setShowMenu(!showMenu)}
        disabled={isExporting}
        title="Export chart"
        aria-label="Export chart"
      >
        <i className={`bi bi-download ${isExporting ? 'spin' : ''}`}></i>
        {isExporting ? 'Exporting...' : 'Export'}
      </button>

      {showMenu && (
        <div className="export-menu-dropdown">
          {formats.map(format => (
            <button
              key={format}
              className="export-menu-item"
              onClick={() => handleExport(format)}
              disabled={isExporting}
            >
              <i className={`bi ${formatIcons[format] || 'bi-file-earmark'}`}></i>
              {formatLabels[format]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

ChartExportButton.propTypes = {
  chartRef: PropTypes.oneOfType([
    PropTypes.instanceOf(HTMLElement),
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLElement) })
  ]).isRequired,
  filename: PropTypes.string,
  formats: PropTypes.arrayOf(PropTypes.oneOf(['png', 'svg', 'pdf'])),
  className: PropTypes.string
};

export default ChartExportButton;

